/**
 * CollapsedSectionPopover Component
 *
 * Flyout popover for collapsed sidebar accordion sections.
 * When the sidebar is in icon-only mode (tablet width), clicking an accordion
 * icon opens this popover to reveal all sub-items that would otherwise be
 * hidden in the collapsed state.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { colors } from '../../theme/colors';
import type { SurfaceKey } from '../../surfaces';
import { TenantMarkedLabel } from './TenantHiddenBadge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CollapsedSectionItem {
  text: string;
  path: string;
  isSelected: boolean;
  /**
   * The item's surface registry key: for a staff caller the item then shows the
   * same small marker icon as the expanded sidebar (D-TSG-26 (2)). Optional -
   * without it the item renders exactly as before.
   */
  surface?: SurfaceKey;
}

export interface CollapsedSectionPopoverProps {
  /** Section title displayed at the top of the popover */
  title: string;
  /** Anchor element for positioning */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Sub-items to display */
  items: CollapsedSectionItem[];
  /** Navigation callback */
  onNavigate: (path: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function itemLabel(item: CollapsedSectionItem): React.ReactElement {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: '13px',
        fontWeight: item.isSelected ? 600 : 400,
        color: item.isSelected
          ? colors.text.selected
          : colors.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {item.text}
    </Typography>
  );
}

const CollapsedSectionPopover: React.FC<CollapsedSectionPopoverProps> = ({
  title,
  anchorEl,
  open,
  onClose,
  items,
  onNavigate,
}) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 180,
            maxWidth: 240,
            ml: 0.5,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid',
            borderColor: 'divider',
          },
        },
      }}
    >
      <Box sx={{ py: 1 }}>
        {/* Section title */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 2,
            py: 0.75,
            color: colors.text.secondary,
            fontWeight: 600,
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {title}
        </Typography>

        {/* Sub-items */}
        {items.map((item) => (
          <Box
            key={item.path}
            component="button"
            onClick={() => {
              onNavigate(item.path);
              onClose();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              px: 2,
              py: 1,
              border: 'none',
              bgcolor: item.isSelected ? 'action.selected' : 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.1s ease',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {item.surface ? (
              <TenantMarkedLabel surface={item.surface}>{itemLabel(item)}</TenantMarkedLabel>
            ) : (
              itemLabel(item)
            )}
          </Box>
        ))}
      </Box>
    </Popover>
  );
};

export default CollapsedSectionPopover;
